/**
 * CreateValidationRuleRenderer - read-only preview for `create_validation_rule` proposals.
 *
 * Displays the rule name, target entity type, condition/constraint formulas,
 * message, and enabled state. Read-only: rich formula editing and syntax
 * highlighting belong to a future bespoke renderer (issue #8915), not here.
 */

import { useOrg } from '@/contexts/OrgContext';
import { Badge } from '@/lib/base/badges/badges';
import { EntityTypeValueLabel } from '@/lib/crm/components/properties/labels/EntityTypeValueLabel';
import { FormulaValueLabel } from '@/lib/crm/components/properties/labels/FormulaValueLabel';
import { cx } from '@/utils/cx';

import type { CreateValidationRulePayload } from '../../../lib/proposals/core/types';
import { isCreateValidationRulePayload } from '../../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../../lib/proposals/registry';
import { ActionError } from '../shared';

interface FormulaBlockProps {
  label: string;
  formula: string;
  targetEntityTypeName?: string;
  orgId?: number;
}

function FormulaBlock({
  label,
  formula,
  targetEntityTypeName,
  orgId,
}: FormulaBlockProps) {
  return (
    <div className='space-y-1'>
      <p className='text-sm font-medium text-fg-secondary'>{label}</p>
      <div className='overflow-x-auto rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2'>
        <FormulaValueLabel
          formula={formula}
          targetEntityTypeName={targetEntityTypeName}
          orgId={orgId}
          className='text-xs text-fg-tertiary'
        />
      </div>
    </div>
  );
}

export function CreateValidationRuleRenderer({
  action,
  mode,
  className,
  orgId: orgIdProp,
}: ActionRendererProps) {
  const { currentOrg } = useOrg();
  const orgId = orgIdProp ?? currentOrg?.id;

  const isValidPayload = isCreateValidationRulePayload(action.payload);
  const payload = isValidPayload
    ? (action.payload as CreateValidationRulePayload)
    : undefined;

  if (!payload) {
    return (
      <ActionError
        message='Invalid create_validation_rule payload'
        mode={mode}
      />
    );
  }

  return (
    <div className={cx('space-y-4 px-4 py-3', className)}>
      <div className='space-y-1'>
        <p className='text-sm font-medium text-fg-secondary'>
          {payload.display_name}
        </p>
        <p className='text-xs text-fg-tertiary'>
          Applies to{' '}
          <EntityTypeValueLabel
            value={payload.target_entity_type_name}
            orgId={orgId}
            fontSize='xs'
          />
        </p>
      </div>

      <FormulaBlock
        label='When'
        formula={payload.condition}
        targetEntityTypeName={payload.target_entity_type_name}
        orgId={orgId}
      />
      <FormulaBlock
        label='Validate'
        formula={payload.constraint}
        targetEntityTypeName={payload.target_entity_type_name}
        orgId={orgId}
      />

      {payload.message ? (
        <div className='space-y-1'>
          <p className='text-sm font-medium text-fg-secondary'>Message</p>
          <p className='text-sm text-fg-secondary'>{payload.message}</p>
        </div>
      ) : null}

      <Badge color={payload.enabled ? 'success' : 'gray'} size='sm'>
        {payload.enabled ? 'Active' : 'Paused'}
      </Badge>
    </div>
  );
}
